/**
 * Logs.tsx — Histórico de conversas com o agente (apenas admin)
 */

import { useEffect, useState } from 'react'
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore'
import { db } from '../lib/firebase'

interface ConvMessage {
  role: 'user' | 'assistant'
  content: string
  cited_nodes?: string[]
  model_used?: string
  ts: { toDate: () => Date } | null
}

interface Conversation {
  id: string
  user_id: string
  started_at: { toDate: () => Date } | null
  messages: ConvMessage[]
  feedback: { thumbs_up: boolean; comment?: string } | null
}

export function Logs() {
  const [convs, setConvs] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    getDocs(
      query(collection(db, 'kb_conversas'), orderBy('started_at', 'desc'), limit(50))
    )
      .then((snap) => {
        setConvs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Conversation)))
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <div
      style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '32px 24px',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <h1 style={{ fontSize: '20px', color: '#e8e8f0', marginBottom: '8px' }}>Logs</h1>
      <p style={{ fontSize: '13px', color: '#5a5a80', marginBottom: '32px' }}>
        Últimas 50 conversas com o agente RAG.
      </p>

      {loading && <div style={{ color: '#5a5a80', fontSize: '13px' }}>Carregando...</div>}

      {convs.map((conv) => (
        <div
          key={conv.id}
          style={{
            marginBottom: '12px',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {/* Header da conversa */}
          <div
            onClick={() => setExpanded((prev) => (prev === conv.id ? null : conv.id))}
            style={{
              padding: '12px 16px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.03)',
              userSelect: 'none',
            }}
          >
            <div>
              <span style={{ fontSize: '12px', color: '#9090b0' }}>
                {conv.started_at
                  ? conv.started_at.toDate().toLocaleString('pt-BR')
                  : '—'}
              </span>
              <span style={{ marginLeft: '16px', fontSize: '12px', color: '#5a5a80' }}>
                {conv.messages?.length ?? 0} mensagens
              </span>
              {conv.feedback && (
                <span style={{ marginLeft: '12px', fontSize: '12px' }}>
                  {conv.feedback.thumbs_up ? '👍' : '👎'}
                </span>
              )}
            </div>
            <span style={{ color: '#5a5a80', fontSize: '12px' }}>
              {expanded === conv.id ? '▲' : '▼'}
            </span>
          </div>

          {/* Mensagens expandidas */}
          {expanded === conv.id && (
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {conv.messages?.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background:
                      msg.role === 'user'
                        ? 'rgba(87, 245, 195, 0.06)'
                        : 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.04)',
                  }}
                >
                  <div style={{ fontSize: '10px', color: '#5a5a80', marginBottom: '4px' }}>
                    {msg.role === 'user' ? 'usuário' : `agente (${msg.model_used ?? '—'})`}
                    {msg.ts ? ` · ${msg.ts.toDate().toLocaleTimeString('pt-BR')}` : ''}
                  </div>
                  <div style={{ fontSize: '13px', color: '#e8e8f0', whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </div>
                  {msg.cited_nodes && msg.cited_nodes.length > 0 && (
                    <div style={{ fontSize: '10px', color: '#57f5c3', marginTop: '6px' }}>
                      Fontes: {msg.cited_nodes.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {!loading && convs.length === 0 && (
        <div style={{ color: '#5a5a80', fontSize: '13px' }}>
          Nenhuma conversa registrada ainda.
        </div>
      )}
    </div>
  )
}
